import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { Focus, inFocus, useFocus } from './focus';
import { DashboardClient, DashboardViewType } from '../../api/api';
import { useIdentity } from '../../auth/useIdentity';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { CockpitEnvironment, EnvKind, getCockpitEnvironments, getEnvironmentsFallback } from './cockpit';
import { usePoll } from './usePoll';

export interface CockpitState {
    /** Every environment this account may see, sorted: worst first within each customer. */
    environments: CockpitEnvironment[];
    /** The environments inside the header's focus, for the overview pages; all of them without a focus. */
    shown: CockpitEnvironment[];
    focus: Focus | null;
    loadedAt?: Date;
    error?: string;
    /** True while the API has no /Cockpit yet and the list comes from the older overviews. */
    fallback: boolean;
    isAdmin: boolean;
    find: (customer?: string, environment?: string) => CockpitEnvironment | undefined;
    ofCustomer: (customer?: string) => CockpitEnvironment[];
    customers: string[];
    lab?: CockpitEnvironment;
    reload: () => void;
}

const CockpitContext = createContext<CockpitState>({
    environments: [],
    shown: [],
    focus: null,
    fallback: false,
    isAdmin: false,
    find: () => undefined,
    ofCustomer: () => [],
    customers: [],
    reload: () => undefined,
});

const KIND_ORDER: Record<EnvKind, number> = { prod: 0, test: 1, lab: 2 };

export const envKind = (e?: { kind?: EnvKind; stage?: string } | null): EnvKind =>
    (e?.kind as EnvKind) ?? (e?.stage === 'prod' ? 'prod' : 'test');

/** What a non-admin may see: the environments of their own customer, from the old dashboard endpoint. */
const customerEnvironments = async (axios: any): Promise<CockpitEnvironment[]> => {
    const client = new DashboardClient(undefined, axios);
    const rows = await client.dashboard({ viewType: DashboardViewType.All });

    return (rows ?? []).map((r) => ({
        customer: r.customerName ?? '',
        environment: r.customerEnvironment ?? '',
        stage: (r.customerEnvironment ?? '').toLowerCase().startsWith('prod') ? 'prod' : 'test',
        kind: (r.customerEnvironment ?? '').toLowerCase().startsWith('prod') ? 'prod' : 'test',
        open: 0,
        waitingOnUs: 0,
        waitingOnCustomer: 0,
        resolvesItself: 0,
        needsRequeue: 0,
        builds: [],
    }));
};

/**
 * The environments every v2 screen and the shell share, loaded once and kept fresh: the scope bar,
 * the rail counts and the overview screens all read the same list, so they never disagree.
 */
export const CockpitProvider = ({ children }: { children: ReactNode }) => {
    const axios = useAxiosSwagger();
    const { isAdmin, status } = useIdentity();
    const [fallback, setFallback] = useState(false);

    const poll = usePoll<CockpitEnvironment[]>(
        axios && status === 'ready'
            ? async (signal) => {
                  if (!isAdmin) return customerEnvironments(axios);

                  try {
                      const rows = await getCockpitEnvironments(axios, signal);
                      setFallback(false);
                      return rows;
                  } catch (e: any) {
                      if (e?.response?.status !== 404) throw e;
                      setFallback(true);
                      return getEnvironmentsFallback(axios, signal);
                  }
              }
            : null,
        20000,
        [axios, isAdmin, status]
    );

    const environments = useMemo(
        () =>
            [...(poll.data ?? [])].sort(
                (a, b) =>
                    a.customer.localeCompare(b.customer) ||
                    KIND_ORDER[envKind(a)] - KIND_ORDER[envKind(b)] ||
                    a.environment.localeCompare(b.environment)
            ),
        [poll.data]
    );

    const focus = useFocus();
    // A focus on a customer or environment that is gone (renamed, no access) shows everything instead of nothing.
    const known = !focus || environments.length === 0 || environments.some((e) => inFocus(focus, e));
    const shown = useMemo(() => (known ? environments.filter((e) => inFocus(focus, e)) : environments), [environments, focus, known]);

    const find = useCallback(
        (customer?: string, environment?: string) =>
            environments.find(
                (e) =>
                    e.customer.toLowerCase() === (customer ?? '').toLowerCase() &&
                    e.environment.toLowerCase() === (environment ?? '').toLowerCase()
            ),
        [environments]
    );

    const ofCustomer = useCallback(
        (customer?: string) => environments.filter((e) => e.customer.toLowerCase() === (customer ?? '').toLowerCase()),
        [environments]
    );

    const value = useMemo<CockpitState>(
        () => ({
            environments,
            shown,
            focus: known ? focus : null,
            loadedAt: poll.loadedAt,
            error: poll.error,
            fallback,
            isAdmin,
            find,
            ofCustomer,
            customers: Array.from(new Set(environments.map((e) => e.customer))),
            // An API without /Cockpit does not say where scenarios run; that has always been c2e test.
            lab:
                environments.find((e) => envKind(e) === 'lab') ??
                (fallback ? environments.find((e) => e.customer.toLowerCase() === 'c2e' && e.environment.toLowerCase() === 'test') : undefined),
            reload: poll.reload,
        }),
        [environments, shown, focus, known, poll.loadedAt, poll.error, poll.reload, fallback, isAdmin, find, ofCustomer]
    );

    return <CockpitContext.Provider value={value}>{children}</CockpitContext.Provider>;
};

export const useCockpit = () => useContext(CockpitContext);

/** The share of the day's Exact calls used, 0-100; 0 when not known. */
export const exactPct = (e?: { exactLimit?: number | null; exactRemaining?: number | null } | null) =>
    e?.exactLimit && e.exactRemaining !== null && e.exactRemaining !== undefined ? ((e.exactLimit - e.exactRemaining) / e.exactLimit) * 100 : 0;

/** One colour rule for the Exact budget on every screen: red when it runs out or is nearly used, amber past half. */
export const exactTone = (e?: { exactRunsOutAt?: string | null; exactLimit?: number | null; exactRemaining?: number | null } | null, usedPct = exactPct(e)): 'bad' | 'warn' | '' =>
    e?.exactRunsOutAt || usedPct > 85 ? 'bad' : usedPct > 55 ? 'warn' : '';

/** The same as a meter colour. */
export const exactMeter = (tone: 'bad' | 'warn' | ''): 'r' | 'w' | '' => (tone === 'bad' ? 'r' : tone === 'warn' ? 'w' : '');

/** What we must do: our issues and the ones waiting to be offered again, counted the same on every screen. */
export const usCount = (e: { waitingOnUs: number; needsRequeue?: number | null }) => e.waitingOnUs + (e.needsRequeue ?? 0);

/** Worst state of an environment, for its tile: bad = we must act, warn = waits or slows, ok = quiet. */
/**
 * Red only when we have to act: our issues, or poison on production. Poison on a test environment is old test traffic
 * more often than not, so it is amber, like what waits on the customer or on Exact.
 */
export const envTone = (e: CockpitEnvironment): 'ok' | 'warn' | 'bad' | 'off' => {
    const prod = envKind(e) === 'prod';
    if (usCount(e) > 0 || (prod && (e.poison ?? 0) > 0)) return 'bad';
    if ((e.poison ?? 0) > 0 || e.waitingOnCustomer > 0 || e.exactRunsOutAt || (e.newProblems ?? 0) > 0 || (e.postponed ?? 0) > 0) return 'warn';
    return 'ok';
};

/** What deviates in an environment, as short labels for its tile; nothing when all is quiet. */
export const envFacts = (e: CockpitEnvironment): { text: string; tone: 'bad' | 'warn' | 'info' }[] => {
    const facts: { text: string; tone: 'bad' | 'warn' | 'info' }[] = [];
    if (usCount(e) > 0) facts.push({ text: `${usCount(e)} wij`, tone: 'bad' });
    if (e.waitingOnCustomer > 0) facts.push({ text: `${e.waitingOnCustomer} klant`, tone: 'warn' });
    if ((e.poison ?? 0) > 0) facts.push({ text: `${e.poison} poison`, tone: envKind(e) === 'prod' ? 'bad' : 'warn' });
    if ((e.postponed ?? 0) > 0) facts.push({ text: `${e.postponed} uitgesteld`, tone: 'warn' });
    if (e.exactRunsOutAt) facts.push({ text: 'Exact raakt op', tone: 'warn' });
    if (e.watchStatus === 'watching') facts.push({ text: 'monitor', tone: 'info' });
    return facts;
};

/** One short label for the tile; the details are on the environment page. */
export const envSummary = (e: CockpitEnvironment): string => {
    if (usCount(e) > 0) return `${usCount(e)} actie nodig`;
    if ((e.poison ?? 0) > 0) return `${e.poison} in poison`;
    if (e.exactRunsOutAt) return 'Remt op Exact';
    if (e.waitingOnCustomer > 0) return 'Wacht op klant';
    if ((e.postponed ?? 0) > 0) return `${e.postponed} uitgesteld`;
    if (e.watchStatus === 'watching') return 'Monitor loopt';
    return 'Rustig';
};
